import { useEffect } from 'react';
import { CSSThemeProvider, useCSSTheme } from '@quinyx/ui';

const THEME_STORAGE_KEY = 'admin-theme';

const getStoredTheme = (): 'light' | 'dark' | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return null;
};

// Component to sync theme changes with localStorage
const ThemeSync = () => {
  const { themeName } = useCSSTheme();

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeName);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [themeName]);

  // This component doesn't render anything, it just syncs state
  return null;
};

interface ThemeProviderWithPersistenceProps {
  children: React.ReactNode;
}

export const ThemeProviderWithPersistence = ({
  children,
}: ThemeProviderWithPersistenceProps) => {
  const storedTheme = getStoredTheme();
  const defaultTheme = storedTheme || 'light';

  return (
    <CSSThemeProvider defaultThemeName={defaultTheme}>
      <ThemeSync />
      {children}
    </CSSThemeProvider>
  );
};
